import { ColumnDef } from "@tanstack/react-table";
import { LifeDataMaintenance } from "../../../../types/formDataTypes";
import { t } from "i18next";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { isUndefined } from "lodash";
import { useState } from "react";
import { Input } from "../../../../ui/Input/Input";
import RoleRequire from "../../../../helpers/auth/RoleRequire";
import { Button } from "../../../../ui/Button";
import MaintenancesFilters, { MaintenancesCategoriesType, MaintenancesFilterType } from "../MaintenancesFilters";
import MaintenanceSorting, { MaintenancesSortingType } from "../MaintenanceSorting";
import { SimpleDataTable } from "../../../../ui/DataTable/SimpleDataTable";
import { useAppStore } from "../../../../stores/RootStore";
import _ from "lodash";
import AddService from "./AddService";
import { TbCheck } from "react-icons/tb";
import EditService from "./EditService";
import { Maintenance } from "../../../../types/types";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogOverlay, DialogTitle } from "../../../../ui/Dialog";


//Сортировачные функции
function compareLuxonDates(a: LifeDataMaintenance, b: LifeDataMaintenance) {
  return a.start.toMillis() - b.start.toMillis();
}

function compareMaintenanceTime(a: LifeDataMaintenance, b: LifeDataMaintenance) {
  return a.data.rest_operating_time - b.data.rest_operating_time;
}

export function ServiceTable({nodes, maintenances}:{nodes: LifeDataMaintenance[], maintenances: Maintenance[]}) {
  const {equipments} = useAppStore()
  const navigate = useNavigate()
  const [selectedService, setSelectedService] = useState<Maintenance>()
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false)
  const [openEditModal, setOpenEditModal] = useState<boolean>(false)
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<MaintenancesFilterType>({
    producers: [],
    equipment_type: [],
    maintenance_type: [],
  });

  const [sorting, setSorting] = useState<MaintenancesSortingType>({
    date: undefined,
    time: undefined,
  });

  const categories: MaintenancesCategoriesType = {
    unique_equipment_type_name: [],
    unique_producer_name: [],
    unique_maintenance_type: [],
  };

  //Создание массивов с уникальными названиями для категорий фильтра
  if (nodes) {
    for (let i = 0; i < nodes.length; i++) {
      const elem = nodes[i];
      if (!categories.unique_equipment_type_name.includes(getEquipmentTypeById(elem.equipment_item_id)!))
        categories.unique_equipment_type_name.push(getEquipmentTypeById(elem.equipment_item_id)!);
      if (!categories.unique_producer_name.includes(getEquipmentProducerById(elem.equipment_item_id)!))
        categories.unique_producer_name.push(getEquipmentProducerById(elem.equipment_item_id)!);
    }
  }

  nodes = _.sortBy(nodes, []);
  let data = { nodes };

  const compactData = [...data.nodes];
  compactData.sort(compareLuxonDates);

  // Получение типа оборудования по его ID
  function getEquipmentTypeById(id: Number) {
    return equipments?.find((eq) => eq.id == String(id))?.attributes.equipment_type_name;
  }

  // Получение производителя по его ID
  function getEquipmentProducerById(id: Number) {
    return equipments?.find((eq) => eq.id == String(id))?.attributes.producer_name;
  }


  const handleSearch = (event: any) => {
    setSearch(event.target.value);
  };

  // Фильтрация через поиск
  data = {
    nodes: data.nodes?.filter(
      (item) =>
        item.equipment_name.toLowerCase().includes(search.toLowerCase()) ||
        item.equipment_inventory_number!.toLowerCase().includes(search.toLowerCase())
    ),
  };

  //Фильтрация через категории
  data = {
    nodes: data.nodes?.filter(
      (item) =>
        (filter.producers.length > 0 ? filter.producers.includes(getEquipmentProducerById(item.equipment_item_id)!) : true) &&
        (filter.equipment_type.length > 0
          ? filter.equipment_type.includes(getEquipmentTypeById(item.equipment_item_id)!)
          : true) &&
        (filter.maintenance_type.length > 0 ? filter.maintenance_type.includes(item.type) : true)
    ),
  };

  //Сортировка
  if (!isUndefined(sorting.date))
    sorting.date ? data.nodes.sort(compareLuxonDates) : data.nodes.sort(compareLuxonDates).reverse();
  if (!isUndefined(sorting.time))
    sorting.time ? data.nodes.sort(compareMaintenanceTime) : data.nodes.sort(compareMaintenanceTime).reverse();

  const columns: ColumnDef<LifeDataMaintenance>[] = [
    {
      header: `${t("maintenance_type")} ${t("equipment_name")}`,
      size: 300,
      cell: ({ row }) => (
        <div className="flex flex-col w-full">
          <div className="w-[150px]">{t("maintenance_" + row.original.type)}</div>
          <div>{`${row.original.equipment_inventory_number}, ${row.original.equipment_name}`}</div>
        </div>
      ),
    },
    {
      header: t("description"),
      size: 1000,
      minSize: 200,
      cell: ({ row }) => (
        <div className="flex flex-col min-w-[100px] truncate">
          <div>{row.original.name}</div>
        </div>
      ),
    },
    {
      header: t("operating_time"),
      size: 100,
      cell: ({ row }) => (
        <div className="flex w-[100px]">
          <div>{row.original.data.operating_time} / {row.original.data.rest_operating_time}</div>
        </div>
      ),
    },
    {
      header: t("date"),
      size: 150,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <div>{row.original.start.toLocaleString(DateTime.DATE_SHORT)}</div>
          <div>{row.original.end.toLocaleString(DateTime.DATE_SHORT)}</div>
        </div>
      ),
    },
    {
      header: t("expired"),
      size: 150,
      cell: ({ row }) => (
        <div>
          <div>{row.original.data.expired ? <TbCheck /> : <></>}</div>
        </div>
      ),
    }
  ];

  function updateNodes() {

  }

  function selectMaintenance(item: any) {
    setSelectedService(maintenances.find(m => String(m.id) === item.id))
    setOpenEditModal(true)
  }
  
  function addMaintenance() {
    setOpenCreateModal(true)
  }

  return (
    <>
      <div className="flex flex-col w-full">
        <div className="flex flex-row w-full h-14 items-center gap-4 px-6 justify-between">
          <Input 
            className="h-8"
            placeholder={t("equipment_search")}
            onChange={handleSearch}
            value={search}
            autoComplete="off"
            width="100%" 
          />
          
          <RoleRequire role="maintainer">
            <Button variant={"default"} size={"md"} onClick={() => setOpenCreateModal(true)}>{t("add")}</Button>
          </RoleRequire>
        </div>
        <div className="flex items-center px-6 mb-2 gap-4 w-full">
          <MaintenancesFilters filterState={[filter, setFilter]} categories={categories} />
          <MaintenanceSorting sortingState={[sorting, setSorting]}/>
        </div>
      </div>
      {!data.nodes?.length ? ( // Заглушка для отсутствия данных при поиске
        <div className="flex w-full h-full justify-center items-center">
          {t("no_data")}
        </div>
      ) : (
        <div className="px-2">
          <SimpleDataTable data={data.nodes} columns={columns} onClickRow={selectMaintenance}/> 
        </div>
      )}
      {openCreateModal && <AddServiceDialog open={openCreateModal} onChange={setOpenCreateModal} updateList={updateNodes}/>}
      {selectedService && openEditModal && <EditServiceDialog service={selectedService!} open={openEditModal} onChange={setOpenEditModal} updateList={updateNodes}/>}
    </>
  )
}


function AddServiceDialog({open, onChange, updateList}:{open: boolean, onChange: (state: boolean) => void,  updateList: Function}) {
  return (
    <Dialog open={open} onOpenChange={() => onChange(!open)}>
      <DialogOverlay className="bg-slate-950/40 data-[state=open]:animate-overlayShow fixed inset-0" />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("adding_equipment_maintenance")}</DialogTitle>
          <DialogDescription>
            {t("adding_equipment_maintenance_desc")}
          </DialogDescription>
          <AddService />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}

function EditServiceDialog({service, open, onChange, updateList}:{service: Maintenance, open: boolean, onChange: (state: boolean) => void, updateList: Function}) {
  return (
    <Dialog open={open} onOpenChange={() => onChange(!open)}>
      <DialogOverlay className="bg-slate-950/40 data-[state=open]:animate-overlayShow fixed inset-0" />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("editing_equipment_maintenance")}</DialogTitle>
          <DialogDescription>
            {t("equipment_maintenance_form_text")}
          </DialogDescription>
          <EditService service={service!} updateList={updateList}/>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}